import { useState, useEffect, useRef } from "react";
import { api } from "../../lib/api";
import { ENV } from "../../config/env";

const formatBytes = (bytes) => {
  if (!bytes && bytes !== 0) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

const getToken = () => localStorage.getItem("admin_token");

const uploadFile = (file, folder, onProgress) =>
  new Promise((resolve, reject) => {
    const formData = new FormData();
    formData.append("file", file);

    const xhr = new XMLHttpRequest();
    xhr.open("POST", `${ENV.API_BASE_URL}/admin/media/upload?folder=${encodeURIComponent(folder)}`);
    xhr.setRequestHeader("Authorization", `Bearer ${getToken()}`);

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
    };

    xhr.onload = () => {
      try {
        const res = JSON.parse(xhr.responseText);
        if (xhr.status >= 200 && xhr.status < 300 && res.success) resolve(res.data);
        else reject(new Error(res.message || "Upload failed"));
      } catch {
        reject(new Error("Invalid server response"));
      }
    };

    xhr.onerror = () => reject(new Error("Network error"));
    xhr.send(formData);
  });

const STATUS = { PENDING: "pending", UPLOADING: "uploading", DONE: "done", ERROR: "error" };

export default function MediaManager() {
  const [folder, setFolder] = useState("");
  const [folderError, setFolderError] = useState("");
  const [s3Folders, setS3Folders] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [uploadQueue, setUploadQueue] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);
  const isUploading = useRef(false);
  const inputRef = useRef(null);
  const suggestionsRef = useRef(null);

  // Fetch existing S3 folders for autocomplete
  useEffect(() => {
    api.get("/admin/media/folders")
      .then(({ data }) => setS3Folders(data.data || []))
      .catch(() => {});
  }, []);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handler = (e) => {
      if (
        !inputRef.current?.contains(e.target) &&
        !suggestionsRef.current?.contains(e.target)
      ) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Auto-clear completed queue after 4 seconds
  useEffect(() => {
    if (uploadQueue.length === 0) return;
    const allSettled = uploadQueue.every(
      (i) => i.status === STATUS.DONE || i.status === STATUS.ERROR
    );
    if (!allSettled) return;
    const timer = setTimeout(() => setUploadQueue([]), 4000);
    return () => clearTimeout(timer);
  }, [uploadQueue]);

  // Launch all pending uploads in parallel
  useEffect(() => {
    if (isUploading.current) return;
    const pending = uploadQueue.filter((i) => i.status === STATUS.PENDING);
    if (pending.length === 0) return;

    isUploading.current = true;

    const updateItem = (id, patch) =>
      setUploadQueue((prev) => prev.map((i) => (i.id === id ? { ...i, ...patch } : i)));

    const promises = pending.map(async (item) => {
      updateItem(item.id, { status: STATUS.UPLOADING, progress: 0 });
      try {
        const result = await uploadFile(item.file, item.folder, (pct) =>
          updateItem(item.id, { progress: pct })
        );
        updateItem(item.id, { status: STATUS.DONE, progress: 100, result });
      } catch (err) {
        updateItem(item.id, { status: STATUS.ERROR, error: err.message });
      }
    });

    Promise.all(promises).then(() => {
      isUploading.current = false;
      // Refresh S3 folders list in case a new folder was created
      api.get("/admin/media/folders")
        .then(({ data }) => setS3Folders(data.data || []))
        .catch(() => {});
    });
  }, [uploadQueue]);

  const enqueueFiles = (selectedFiles) => {
    const trimmed = folder.trim();
    if (!trimmed) {
      setFolderError("Enter a folder name before uploading.");
      return;
    }
    setFolderError("");
    const items = Array.from(selectedFiles).map((file) => ({
      id: `${file.name}-${Date.now()}-${Math.random()}`,
      file,
      folder: trimmed,
      status: STATUS.PENDING,
      progress: 0,
      result: null,
      error: null,
    }));
    setUploadQueue((prev) => [...prev, ...items]);
  };

  const handleFileSelect = (e) => {
    if (e.target.files?.length) enqueueFiles(e.target.files);
    e.target.value = "";
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files?.length) enqueueFiles(e.dataTransfer.files);
  };

  const filteredSuggestions = folder.trim()
    ? s3Folders.filter((f) =>
        f.toLowerCase().includes(folder.trim().toLowerCase()) && f !== folder.trim()
      )
    : s3Folders;

  const isActive = uploadQueue.length > 0;

  return (
    <div className="p-4 sm:p-8 max-w-2xl mx-auto">
      <h1 className="text-2xl font-bold mb-8 text-gray-800">Media Manager</h1>

      {/* Folder name input with autocomplete */}
      <div className="mb-6 relative">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          S3 Folder Name
        </label>

        <div ref={inputRef}>
          <input
            type="text"
            value={folder}
            onChange={(e) => {
              setFolder(e.target.value);
              setFolderError("");
              setShowSuggestions(true);
            }}
            onFocus={() => setShowSuggestions(true)}
            onKeyDown={(e) => {
              if (e.key === "Escape") setShowSuggestions(false);
            }}
            placeholder="e.g. cityAirports"
            autoComplete="off"
            className={`w-full border rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              folderError ? "border-red-400" : "border-gray-300"
            }`}
          />
        </div>

        {/* Suggestions dropdown */}
        {showSuggestions && filteredSuggestions.length > 0 && (
          <ul
            ref={suggestionsRef}
            className="absolute z-10 w-full bg-white border border-gray-200 rounded-lg shadow-lg mt-1 max-h-52 overflow-y-auto"
          >
            {filteredSuggestions.map((f) => (
              <li key={f}>
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    setFolder(f);
                    setFolderError("");
                    setShowSuggestions(false);
                  }}
                  className="w-full text-left px-4 py-2 text-sm hover:bg-blue-50 flex items-center gap-2"
                >
                  <span className="text-gray-400 text-xs">📁</span>
                  <span className="text-gray-800">{f}</span>
                  <span className="ml-auto text-xs text-blue-500">existing</span>
                </button>
              </li>
            ))}
          </ul>
        )}

        {folderError && <p className="text-xs text-red-500 mt-1">{folderError}</p>}
        {folder.trim() && !folderError && (
          <p className="text-xs text-gray-400 mt-1">
            Uploads to&nbsp;
            <span className="font-mono text-gray-600">{folder.trim()}/original_image/</span>
            &nbsp;and&nbsp;
            <span className="font-mono text-gray-600">{folder.trim()}/converted_image/</span>
          </p>
        )}
      </div>

      {/* Upload drop zone */}
      <div
        onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        className={`border-2 border-dashed rounded-xl p-6 sm:p-10 text-center cursor-pointer transition-colors ${
          isDragging
            ? "border-blue-400 bg-blue-50"
            : "border-gray-300 hover:border-blue-400 hover:bg-gray-50"
        }`}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/*"
          className="hidden"
          onChange={handleFileSelect}
        />
        <div className="text-5xl mb-3">🖼️</div>
        <p className="text-gray-700 font-medium text-base">Drop images here or click to browse</p>
        <p className="text-xs text-gray-400 mt-1">
          JPG · PNG · GIF · WebP · BMP · TIFF · AVIF &nbsp;·&nbsp; Max 50 MB per file
        </p>
      </div>

      {/* Upload progress — only visible while queue is active, auto-clears after 4s */}
      {isActive && (
        <div className="mt-6 bg-white rounded-xl border divide-y max-h-[520px] overflow-y-auto">
          {uploadQueue.map((item) => (
            <div key={item.id} className="px-4 py-3 space-y-1.5">
              <div className="flex items-center justify-between text-sm">
                <span className="truncate max-w-xs font-medium text-gray-800" title={item.file.name}>
                  {item.file.name}
                </span>
                <span className={`ml-3 shrink-0 text-xs font-semibold ${
                  item.status === STATUS.DONE      ? "text-green-600"
                  : item.status === STATUS.ERROR   ? "text-red-500"
                  : item.status === STATUS.UPLOADING ? "text-blue-600"
                  : "text-gray-400"
                }`}>
                  {item.status === STATUS.DONE      && "✓ Done"}
                  {item.status === STATUS.ERROR     && "✕ Failed"}
                  {item.status === STATUS.UPLOADING && `${item.progress}%`}
                  {item.status === STATUS.PENDING   && "Waiting…"}
                </span>
              </div>

              {(item.status === STATUS.UPLOADING || item.status === STATUS.DONE) && (
                <div className="w-full bg-gray-100 rounded-full h-1.5 overflow-hidden">
                  <div
                    className={`h-1.5 rounded-full transition-all duration-200 ${
                      item.status === STATUS.DONE ? "bg-green-500" : "bg-blue-500"
                    }`}
                    style={{ width: `${item.progress}%` }}
                  />
                </div>
              )}

              {item.status === STATUS.DONE && item.result && (
                <p className="text-xs text-gray-500">
                  {formatBytes(item.result.originalSize)}
                  <span className="text-green-600"> → {formatBytes(item.result.convertedSize)} WebP</span>
                  <span className="ml-2 text-gray-400">· {item.result.folder}</span>
                </p>
              )}

              {item.status === STATUS.ERROR && (
                <p className="text-xs text-red-500">{item.error}</p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
